import { getDashboardBySlug, ReportingCategory } from './reporting-dashboards';

/**
 * Maps Control Tower screens to their Notion documentation pages.
 * DOCUMENTATION_LINKS is matched in order (first match wins), so list a
 * specific route before its parent. Reporting screens resolve by dashboard
 * category via REPORTING_CATEGORY_DOCS.
 */

export const DOCS_BASE_URL = 'https://controltowerdocs.notion.site';

export interface DocumentationTarget {
    /** Full Notion page URL — opened in a new browser tab on desktop. */
    url: string;
    /** Notion page id — used to build the in-app embed URL on mobile. */
    id: string;
}

/** Root documentation page. Used when no screen-specific page is mapped. */
export const DOCS_ROOT: DocumentationTarget = {
    url: DOCS_BASE_URL,
    id: '2d15769c771a8073b997ed29ea3afa17',
};

interface DocumentationRoute extends DocumentationTarget {
    /**
     * Route matcher. A string matches when the current path starts with it;
     * a RegExp matches when it tests true against the path. Evaluated in
     * array order — list more specific routes before their parents.
     */
    pattern: string | RegExp;
}

export const DOCUMENTATION_LINKS: DocumentationRoute[] = [
    {
        pattern: '/receiving',
        url: `${DOCS_BASE_URL}/Receiving-2e65769c771a802898dff4b6ee572b61`,
        id: '2e65769c771a802898dff4b6ee572b61',
    },
    {
        pattern: '/putaway',
        url: `${DOCS_BASE_URL}/Put-Away-2e65769c771a8039bbbfe823f3353c7b`,
        id: '2e65769c771a8039bbbfe823f3353c7b',
    },
    // Picking sub-routes must precede the `/picking` parent.
    {
        pattern: '/picking/manage/will-call',
        url: `${DOCS_BASE_URL}/Manage-Will-Calls-2e65769c771a80b7a04fececf121c4ed`,
        id: '2e65769c771a80b7a04fececf121c4ed',
    },
    {
        pattern: '/picking/manage/hot-shot',
        url: `${DOCS_BASE_URL}/Manage-Hot-Shot-2e65769c771a8056bfd2dd3b5e14179d`,
        id: '2e65769c771a8056bfd2dd3b5e14179d',
    },
    {
        pattern: '/picking',
        url: `${DOCS_BASE_URL}/Picking-2e15769c771a8086977cfa328f115a9f`,
        id: '2e15769c771a8086977cfa328f115a9f',
    },
    {
        pattern: '/packing',
        url: `${DOCS_BASE_URL}/Packing-2e65769c771a8083927ade8e3d5855da`,
        id: '2e65769c771a8083927ade8e3d5855da',
    },
    {
        pattern: '/cycle-counts',
        url: `${DOCS_BASE_URL}/Cycle-Counts-2e65769c771a80dd802fc44d93769a16`,
        id: '2e65769c771a80dd802fc44d93769a16',
    },
    {
        pattern: '/replenishments',
        url: `${DOCS_BASE_URL}/Replenishments-2e65769c771a80bb8a32e7b9b970dfbd`,
        id: '2e65769c771a80bb8a32e7b9b970dfbd',
    },
    {
        pattern: '/exceptions',
        url: `${DOCS_BASE_URL}/Exceptions-2e65769c771a8074a683ec54d5955c1c`,
        id: '2e65769c771a8074a683ec54d5955c1c',
    },

    // Zones/Slots/Handling Units lives under a warehouse id, so it must be
    // matched before the `/wms/warehouses` parent.
    {
        pattern: /^\/wms\/warehouses\/[^/]+\/structure/,
        url: `${DOCS_BASE_URL}/Zones-Slots-and-Handling-Units-2dc5769c771a80f4872bdb645bae4833`,
        id: '2dc5769c771a80f4872bdb645bae4833',
    },
    {
        pattern: '/wms/warehouses',
        url: `${DOCS_BASE_URL}/Manage-Warehouses-2e15769c771a8010bd98ea55e26df249`,
        id: '2e15769c771a8010bd98ea55e26df249',
    },
    {
        pattern: '/wms/inventory',
        url: `${DOCS_BASE_URL}/Manage-Inventory-2e65769c771a80af8e80d7532c5fbf9f`,
        id: '2e65769c771a80af8e80d7532c5fbf9f',
    },

    {
        pattern: '/manage/item-catalog',
        url: `${DOCS_BASE_URL}/Manage-Items-2e65769c771a80daad08cb131f0b8093`,
        id: '2e65769c771a80daad08cb131f0b8093',
    },
    {
        pattern: '/manage/vendors',
        url: `${DOCS_BASE_URL}/Vendors-2e65769c771a804baa9be55d34596aba`,
        id: '2e65769c771a804baa9be55d34596aba',
    },
    {
        pattern: '/manage/contacts',
        url: `${DOCS_BASE_URL}/Contacts-2e65769c771a8017a197e2d928a775f9`,
        id: '2e65769c771a8017a197e2d928a775f9',
    },
    {
        pattern: '/manage/user-positions',
        url: `${DOCS_BASE_URL}/User-Positions-2e65769c771a808685d4ca216b107360`,
        id: '2e65769c771a808685d4ca216b107360',
    },
    {
        pattern: '/manage/carriers',
        url: `${DOCS_BASE_URL}/Carriers-2e65769c771a80299348c31d84e4381b`,
        id: '2e65769c771a80299348c31d84e4381b',
    },
    {
        pattern: '/manage/users',
        url: `${DOCS_BASE_URL}/Users-2e65769c771a801196c4e168c70fcbc1`,
        id: '2e65769c771a801196c4e168c70fcbc1',
    },
    {
        pattern: '/manage/locations',
        url: `${DOCS_BASE_URL}/Locations-2e65769c771a80ef92d9d36a8e8c329c`,
        id: '2e65769c771a80ef92d9d36a8e8c329c',
    },
    {
        pattern: '/manage/roles',
        url: `${DOCS_BASE_URL}/Roles-Permissions-2e65769c771a80ff9d28dbc2d087d856`,
        id: '2e65769c771a80ff9d28dbc2d087d856',
    },
];

/** Reporting documentation pages, keyed by dashboard category. */
export const REPORTING_CATEGORY_DOCS: Record<
    ReportingCategory,
    DocumentationTarget
> = {
    [ReportingCategory.OverallKPIs]: {
        url: `${DOCS_BASE_URL}/Reporting-Overall-KPIs-3515769c771a811381acf5f7f671bb06`,
        id: '3515769c771a811381acf5f7f671bb06',
    },
    [ReportingCategory.Exceptions]: {
        url: `${DOCS_BASE_URL}/Reporting-Exceptions-3515769c771a81bba8b9e1adb238aa34`,
        id: '3515769c771a81bba8b9e1adb238aa34',
    },
    [ReportingCategory.Auditing]: {
        url: `${DOCS_BASE_URL}/Reporting-Auditing-3515769c771a81eca073d1039e571ccf`,
        id: '3515769c771a81eca073d1039e571ccf',
    },
    [ReportingCategory.Inbound]: {
        url: `${DOCS_BASE_URL}/Reporting-Inbound-3515769c771a8178a17fcdf78f17d992`,
        id: '3515769c771a8178a17fcdf78f17d992',
    },
    [ReportingCategory.Outbound]: {
        url: `${DOCS_BASE_URL}/Reporting-Outbound-3515769c771a8118934eeef497843520`,
        id: '3515769c771a8118934eeef497843520',
    },
    [ReportingCategory.Workforce]: {
        url: `${DOCS_BASE_URL}/Reporting-Workforce-3515769c771a81e49486ceda13173ecb`,
        id: '3515769c771a81e49486ceda13173ecb',
    },
    [ReportingCategory.Inventory]: {
        url: `${DOCS_BASE_URL}/Reporting-Inventory-3515769c771a8187a38ddf0f35807054`,
        id: '3515769c771a8187a38ddf0f35807054',
    },
    [ReportingCategory.Warehouse]: {
        url: `${DOCS_BASE_URL}/Reporting-Warehouse-3515769c771a81eb9dc0cdd7f2fd20c4`,
        id: '3515769c771a81eb9dc0cdd7f2fd20c4',
    },
    [ReportingCategory.Suppliers]: {
        url: `${DOCS_BASE_URL}/Reporting-Suppliers-3515769c771a81cc8ae5ffcb38233dd9`,
        id: '3515769c771a81cc8ae5ffcb38233dd9',
    },
};

const REPORTING_PATH_PREFIX = '/reporting/';

/**
 * Resolves the documentation target for a router URL. Reporting routes resolve
 * by their dashboard category; everything else uses {@link DOCUMENTATION_LINKS}.
 * Falls back to {@link DOCS_ROOT} when no screen-specific page is mapped.
 */
export function resolveDocumentationTarget(
    currentUrl: string | null | undefined
): DocumentationTarget {
    if (!currentUrl) {
        return DOCS_ROOT;
    }

    // Strip query string and fragment.
    const path = currentUrl.split(/[?#]/)[0];

    // Reporting resolves by the dashboard's category, so new dashboards are
    // covered automatically without touching DOCUMENTATION_LINKS.
    if (path.startsWith(REPORTING_PATH_PREFIX)) {
        const slug = path.slice(REPORTING_PATH_PREFIX.length).split('/')[0];
        const dashboard = slug ? getDashboardBySlug(slug) : undefined;
        return dashboard
            ? REPORTING_CATEGORY_DOCS[dashboard.category] ?? DOCS_ROOT
            : DOCS_ROOT;
    }

    for (const link of DOCUMENTATION_LINKS) {
        const matches =
            typeof link.pattern === 'string'
                ? path.startsWith(link.pattern)
                : link.pattern.test(path);
        if (matches) {
            return { url: link.url, id: link.id };
        }
    }

    return DOCS_ROOT;
}

/** Builds the Notion in-app embed URL for a page id. */
export function toDocumentationEmbedUrl(id: string): string {
    return `${DOCS_BASE_URL}/ebd/${id}`;
}
